import { cloneFilletFeatureDefinitionData } from './cloneBlendFeatureDefinitionData';

/** 面圆角缺省尺寸，单位与模型长度单位保持一致。 */
const DEFAULT_FACE_FILLET_VALUE = 10;

/**
 * 判断面圆角模式当前是否已接通求解。
 *
 * @param mode 面圆角模式。
 * @returns 是否已接通求解。
 */
export function isSupportedFaceFilletMode(mode: FaceFilletMode | undefined): boolean {
  return mode === undefined || mode === 'symmetric' || mode === 'chord-width' || mode === 'asymmetric';
}

/**
 * 归一化面圆角模式相关字段。
 *
 * 设计说明：
 * 1. SolidWorks 的面圆角各模式拥有不同参数域，不能让隐藏字段继续影响提交和求解；
 * 2. 对称模式使用 `radius`，弦宽度模式使用 `chordWidth`，非对称模式使用 `radius/radius2`；
 * 3. 包络控制线暂未实现，不进入 FaceFilletMode 协议，避免隐藏入口继续污染提交定义。
 *
 * @param definition 原始圆角定义。
 * @param mode 目标面圆角模式。
 * @returns 归一化后的圆角定义。
 */
export function normalizeFilletDefinitionForFaceFilletMode(
  definition: FilletFeatureDefinition,
  mode: FaceFilletMode,
): FilletFeatureDefinition {
  const baseDefinition = cloneFilletFeatureDefinitionData(definition);
  const radius = typeof baseDefinition.radius === 'number' && Number.isFinite(baseDefinition.radius) && baseDefinition.radius > 0
    ? baseDefinition.radius
    : DEFAULT_FACE_FILLET_VALUE;
  const chordWidth = typeof baseDefinition.chordWidth === 'number'
    && Number.isFinite(baseDefinition.chordWidth)
    && baseDefinition.chordWidth > 0
    ? baseDefinition.chordWidth
    : radius;

  return {
    ...baseDefinition,
    type: 'face',
    faceFilletMode: mode,
    radius: mode === 'chord-width' ? undefined : radius,
    radiusExpression: mode === 'chord-width'
      ? null
      : (baseDefinition.radiusExpression ?? null),
    radius2: mode === 'asymmetric'
      ? (
        typeof baseDefinition.radius2 === 'number' && Number.isFinite(baseDefinition.radius2) && baseDefinition.radius2 > 0
          ? baseDefinition.radius2
          : radius
      )
      : undefined,
    radius2Expression: mode === 'asymmetric' ? (baseDefinition.radius2Expression ?? null) : null,
    chordWidth: mode === 'chord-width' ? chordWidth : undefined,
    chordWidthExpression: mode === 'chord-width'
      ? (baseDefinition.chordWidthExpression ?? baseDefinition.radiusExpression ?? null)
      : null,
    faceFilletRadiiFlipped: mode === 'asymmetric'
      ? baseDefinition.faceFilletRadiiFlipped === true
      : false,
    boundaryItems: [],
    boundaryRefs: [],
    trimSurfaces: baseDefinition.trimSurfaces !== false,
    constantWidth: false,
  };
}
